/* eslint-disable imports-autofix/no-restricted-imports */
import type { AxiosError } from "axios"
import type {
  Control,
  FieldError,
  FieldPath,
  FieldValues,
  Path,
  RegisterOptions,
  SubmitHandler,
  UseFormProps,
} from "react-hook-form"
import { useCallback } from "react"
import {
  Controller,
  FormProvider,
  useController,
  useFieldArray,
  useFormContext,
  useForm as useReactHookForm,
} from "react-hook-form"

export {
  Controller,
  FormProvider,
  useController,
  useFieldArray,
  useFormContext,
}
export type {
  Control,
  FieldError,
  FieldPath,
  FieldValues,
  RegisterOptions,
  SubmitHandler,
}

interface ValidationError<Y extends FieldValues = FieldValues> {
  info: {
    message: string
  }
  payload: {
    description: string[]
    errors: {
      input: Path<Y>
      message: string
    }[]
  }
}

export const useForm = <Y extends FieldValues = FieldValues>({
  ...props
}: UseFormProps<Y> = {}) => {
  const methods = useReactHookForm<Y>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    ...props,
  })
  const { setError, setFocus } = methods

  const setAPIErrors = useCallback(
    ({ response }: AxiosError<ValidationError<Y>>) => {
      const errors = response?.data.payload.errors ?? []

      if (!errors.length) return

      errors.forEach(({ input, message }) => {
        setError(input, { message })
      })

      if (errors[0]?.input) setFocus(errors[0].input)
    },
    [setError, setFocus],
  )

  return { ...methods, setAPIErrors }
}

export type UseFormReturn<Y extends FieldValues = FieldValues> = ReturnType<
  typeof useForm<Y>
>
